import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { authFetch } from "../utils/authFetch";
import QRCode from "qrcode";

export default function PaymentPage() {
  const { txnid } = useParams();
  const navigate = useNavigate();

  const [tab, setTab] = useState("placeholder");
  const [seconds, setSeconds] = useState(300);
  const [txnData, setTxnData] = useState(null);
  const [qrImage, setQrImage] = useState("");
  const [isFinal, setIsFinal] = useState(false);

  const timerRef = useRef(null);
  const pollRef = useRef(null);
  const stopFlow = useRef(false);

  const [utr, setUtr] = useState("");
  const [claimLoading, setClaimLoading] = useState(false);

  // ---------------- FETCH TXN ----------------
  useEffect(() => {
    if (!txnid) return;

    const fetchTxn = async () => {
      try {
        const res = await authFetch(`/api/v1/report/get_txnv2/${txnid}`);
        const data = await res.json();

        if (data.status === "success") {
          setTxnData(data);
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchTxn();
  }, [txnid]);

  // ---------------- PRECHECK ----------------
  useEffect(() => {
    async function preCheck() {
      try {
        const res = await fetch(`/api/v1/report/get_txn_check/${txnid}`);
        const result = await res.json();

        const status = result?.data?.order_status;

        if (!status) return;

        if (status === "success") {
          window.location.href = result.data.success;
        }

        if (status === "failed") {
          window.location.href = result.data.failed;
        }
      } catch (err) {
        console.error(err);
      }
    }

    preCheck();
  }, [txnid]);

  // ---------------- GET UPI ----------------
  const upiLink = txnData?.deeplink || txnData?.deeplink_url;

  // ---------------- QR GENERATION ----------------
  useEffect(() => {
    if (!upiLink || isFinal) return;

    QRCode.toDataURL(upiLink)
      .then(setQrImage)
      .catch(console.error);
  }, [upiLink, isFinal]);

  // ---------------- AUTO INTENT (MOBILE) ----------------
  useEffect(() => {
    if (!upiLink) return;

    const isMobile = /android|iphone|ipad|ipod/i.test(navigator.userAgent);

    if (isMobile) {
      setTab("intent");

      // optional auto open
      setTimeout(() => {
        window.location.href = upiLink;
      }, 1200);
    } else {
      setTab("qr");
    }
  }, [upiLink]);

  // ---------------- TIMER ----------------
  useEffect(() => {
    clearInterval(timerRef.current);

    if (tab === "placeholder") return;

    timerRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);

          stopFlow.current = true;
          authFetch(`/api/v1/report/expire/${txnid}`).catch(() => {});

          navigate("/failed");
          return 0;
        }
        return s - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [tab, txnid]);

  // ---------------- POLLING ----------------
  useEffect(() => {
    if (!txnid) return;

    clearInterval(pollRef.current);

    pollRef.current = setInterval(async () => {
      try {
        const res = await authFetch(`/api/v1/report/get_txn_check/${txnid}`);
        const data = await res.json();

        const status = data?.data?.order_status?.toLowerCase();

        if (status === "success") {
          clearInterval(pollRef.current);
          navigate("/success");
        }

        if (status === "failed") {
          clearInterval(pollRef.current);
          navigate("/failed");
        }
      } catch (err) {
        console.error(err);
      }
    }, 5000);

    return () => clearInterval(pollRef.current);
  }, [txnid]);

  // ---------------- HELPERS ----------------
  const fmt = (s) =>
    `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(
      2,
      "0"
    )}`;

  const amount = txnData?.amount || 0;

  const TabBtn = ({ id, icon, label }) => (
    <button
      className={`tab-btn ${tab === id ? "active" : ""}`}
      onClick={() => setTab(id)}
    >
      <i className={`bi ${icon}`}></i> {label}
    </button>
  );


  const claimPayment = async () => {
    try {
      if (!utr.trim()) {
        alert("Please enter UTR");
        return;
      }

      setClaimLoading(true);

      const res = await authFetch(
        `/payin/TOP/claim-wallet?txnid=${txnid}&utr=${utr}`,
        {
          method: "POST"
        }
      );

      const data = await res.json();

      if (data.success) {
        // alert("Payment claimed successfully");
        console.log("Payment claimed successfully");
        navigate("/success");
      } else {
        // alert(data.message || "Claim failed");
        console.error(data.message || "Claim failed");
      }
    } catch (err) {
      console.error(err);
      // alert("Something went wrong");
    } finally {
      setClaimLoading(false);
    }
  };

  return (
    <div className="bg-lock-screen">
      <div className="authentication-lock-screen d-flex align-items-center justify-content-center">
        <div className="container py-4" style={{ maxWidth: 550 }}>

          <style>{`
            .tab-btn{width:100%;background:#fff;border:1px solid #ddd;padding:12px;text-align:left;border-radius:4px;margin-bottom:10px;transition:.3s}
            .tab-btn.active{border-color:#2874f0;background:#e3f0ff;color:#2874f0;font-weight:600}
            .tab-btn i{margin-right:8px;color:#2874f0}
          `}</style>

          <div className="card shadow">
            <div className="card-body">

              <h5 className="mb-3 d-flex justify-content-between">
                Complete Payment
                <span>₹{Number(amount).toFixed(2)}</span>
              </h5>

              <div className="row g-4">

                {/* LEFT */}
                <div className="col-md-4">
                  <TabBtn id="qr" icon="bi-qr-code" label="QR CODE" />
                  <TabBtn id="intent" icon="bi-phone" label="UPI INTENT" />
                </div>

                {/* RIGHT */}
                <div className="col-md-8">

                  <div className="text-danger text-center mb-2">
                    ⏳ Expires in {fmt(seconds)}
                  </div>

                  {/* PLACEHOLDER */}
                  {tab === "placeholder" && (
                    <div className="text-center">
                      <p>Select payment mode</p>
                    </div>
                  )}

                  {/* QR */}
                  {tab === "qr" && (
                    <div className="text-center">
                      <h5>Scan & Pay</h5>

                      {qrImage ? (
                        <img src={qrImage} style={{ width: 180 }} />
                      ) : (
                        <p>Loading QR...</p>
                      )}
                    </div>
                  )}

                  {/* INTENT */}
                  {tab === "intent" && (
                    <div className="text-center">
                      <h5>Pay via UPI App</h5>

                      {upiLink && (
                        <a
                          href={upiLink}
                          className="btn btn-success mt-3"
                        >
                          Pay Now
                        </a>
                      )}
                    </div>

                    
                  )}
                  
                    <h6 className="mt-4 text-center justify-content-center">Trusted by:</h6>
                      <div className="d-flex justify-content-center flex-wrap gap-3 mt-2">
                        {["gpay", "phonepe", "paytm", "bhim"].map((k) => (
                          <img
                            key={k}
                            src={`/assets/${k}-48.png`}
                            alt={k}
                            style={{ width: 30, height: 30 }}
                          />
                        ))}
                      </div>


                      <div className="mt-4">
                        <label className="form-label">
                          Enter UTR Number
                        </label>

                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter UTR"
                          value={utr}
                          onChange={(e) => setUtr(e.target.value)}
                        />

                        <button
                          className="btn btn-primary mt-3 w-100"
                          onClick={claimPayment}
                          disabled={claimLoading}
                        >
                          {claimLoading ? "Processing..." : "Claim Payment"}
                        </button>
                      </div>
                  
                      
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
    </div>
  );
}